"use client"

import { useEffect, useRef } from 'react'

type TrackFn = (event: string, props?: Record<string, any>) => void

// Универсальный трекер: посылает в PostHog + Plausible + Umami если они есть
function track(event: string, props?: Record<string, any>) {
  if (typeof window === 'undefined') return

  const w = window as any

  // PostHog
  try {
    w.posthog?.capture?.(event, props)
  } catch {}

  // Plausible (custom events)
  try {
    w.plausible?.(event, { props })
  } catch {}

  // Umami
  try {
    w.umami?.track?.(event, props)
  } catch {}
}

/**
 * Слушает PWA-события и репортит их в аналитику.
 * Вешаем один раз в root — например в layout лэндинга.
 */
export function useInstallAnalytics() {
  const reported = useRef({ standalone: false })

  useEffect(() => {
    if (typeof window === 'undefined') return

    // 1. Открытие в standalone (косвенный признак установки, единственный способ для iOS)
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true

    if (isStandalone && !reported.current.standalone) {
      reported.current.standalone = true
      track('pwa_open_from_home', {
        platform: /iphone|ipad|ipod/i.test(navigator.userAgent) ? 'ios' :
                  /android/i.test(navigator.userAgent) ? 'android' : 'desktop',
      })
    }

    // 2. Chrome показал возможность установки (beforeinstallprompt)
    const onPromptable = () => {
      track('pwa_install_prompt_available', {
        platform: /android/i.test(navigator.userAgent) ? 'android' : 'desktop',
      })
    }
    window.addEventListener('beforeinstallprompt', onPromptable)

    // 3. Установка завершена (Android/Chrome/Edge)
    const onInstalled = () => {
      track('pwa_install_completed', {
        platform: /android/i.test(navigator.userAgent) ? 'android' : 'desktop',
      })
    }
    window.addEventListener('appinstalled', onInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', onPromptable)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])
}